import React, { useCallback } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { failure } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';

// Components
import Stripe from '~/components/Stripe/Stripe';
import PaymentMethod from '~/components/Account/Wallet/PaymentMethod';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

// MUI Components
import { Backdrop, CircularProgress, Typography } from '@mui/material';

const { TITLE, CLOSE_BUTTON } = MESSAGES.ORDERS.CREATE.PAYMENT_METHOD_REQUIRED;

const FormLoader = () => (
  <Backdrop open sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
    <CircularProgress sx={{ color: '#ed5c2f' }} />
  </Backdrop>
);

/**
 * Blocks order placement until the account has a card on file.
 * Closing the modal (including after the card is saved) never submits the order,
 * so the user lands back on order confirmation with their order intact.
 * Only org admins can add a card, so everyone else gets an explanation instead of a form.
 */
const PaymentMethodRequiredModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const isOrgAdmin = useSelector((state) => state.userReducers.user?.isOrgAdmin) || false;

  const handleFormError = useCallback(() => {
    dispatch(failure(MESSAGES.ACCOUNT.WALLET.STRIPE.PAYMENT_METHOD_FORM_ERROR));
    onClose();
  }, [dispatch, onClose]);

  if (!isOrgAdmin) {
    return (
      <OlcFormModal
        open={open}
        onClose={onClose}
        title={TITLE}
        footer={
          <OlcButton variant="primary" onClick={onClose}>
            {CLOSE_BUTTON}
          </OlcButton>
        }
      >
        <Typography>{MESSAGES.ACCOUNT.WALLET.STRIPE.UNAUTHORIZED_ADD_PAYMENT_METHOD}</Typography>
      </OlcFormModal>
    );
  }

  // Mounted only while open so a setup intent is created on demand, not on every page view.
  return open ? (
    <Stripe loadingFallback={<FormLoader />} onError={handleFormError}>
      <PaymentMethod open={open} handleClose={onClose} />
    </Stripe>
  ) : null;
};

export default PaymentMethodRequiredModal;
